/* eslint-disable unused-imports/no-unused-vars,@typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/prefer-for-of,@typescript-eslint/prefer-nullish-coalescing,@typescript-eslint/no-unsafe-return */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type Values<S> = Pick<S, keyof S>;

interface Props<S> {
  key: string;
  path: number[];
  values: Values<S>;
  isSelected: boolean;
}

interface Cell {
  key: string;
  props: UseTableCellProps<any, any>;
  // 型を軽量化するためにanyを使用
  style: Record<string, any>;
  render: () => React.ReactElement<any, any> | null;
  Content: (
    props: React.HTMLProps<HTMLDivElement> & UseTableCellProps<any, any>,
  ) => React.ReactElement<any, any> | null;
}
interface Row<S> extends Props<S> {
  cells: Cell[];
}

type Rows<S> = Row<S>[];

export interface UseTableCellProps<S, K extends keyof S> {
  cell: {
    value: S[K];
  };
  row: Props<S>;
}

let isAllRowSelected = false;

export type TableCell<S, K extends keyof S> = (
  props: UseTableCellProps<S, K>,
) => React.JSX.Element;

interface ColumnLayoutProperties {
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  align?: React.CSSProperties["textAlign"];
  cellAlign?: React.CSSProperties["textAlign"];
}

type Column<S, K extends keyof S> = {
  Header:
    | string
    | ((props: {
        toggleAllRowsSelected: () => void;
        filterValues: Values<S>;
        setFilters: (values: Values<S>) => void;
        column: {
          filterValue: S[K];
          setFilter: (value: S[K]) => void;
        };
      }) => React.JSX.Element);
  Cell?: (props: UseTableCellProps<S, K>) => React.JSX.Element;
  cellProps?: {
    style?: React.CSSProperties;
  };
  headerProps?: {
    style?: React.CSSProperties;
  };
  filter?: (
    value: S[K],
    args: {
      column: {
        filterValue: S[K];
      };
    },
  ) => boolean;
} & ColumnLayoutProperties;

export type TableColumns<S> = {
  [K in keyof S]?: Column<S, K>;
};

export type TableDataItem = Record<string, any>;

export const useTable = <S extends TableDataItem>({
  data,
  ...props
}: {
  columns: {
    [K in keyof S]?: Column<S, K>;
  };
  data: S[];
  initialState?: { pageIndex?: number; pageSize?: number };
}) => {
  const columns = useRef(props.columns);

  const [filterValues, setFilters] = useState({} as Values<S>);

  const makeSetFilter = (accessor: string) => (value: any) => {
    setFilters((prev) => {
      return {
        ...prev,
        [accessor]: value,
      };
    });
  };

  const toggleAllRowsSelected = useCallback(() => {
    // eslint-disable-next-line react-hooks/immutability
    setRows((prev) => {
      const copy = prev.slice();
      isAllRowSelected = !isAllRowSelected;
      return copy.map((row) => ({
        ...row,
        isSelected: isAllRowSelected,
      }));
    });
  }, []);

  const headers = useMemo(() => {
    return Object.entries(columns.current).map(
      ([accessor, { Header, width, minWidth, maxWidth, align, headerProps }]: [
        string,
        Column<any, any>,
      ]) => {
        const result: {
          key: string;
          style: Record<string, any>;
          render: () => React.JSX.Element | string;
        } = {
          key: accessor,
          style: {
            width,
            minWidth,
            maxWidth,
            textAlign: align,
            ...(headerProps?.style ?? {}),
          },
          render:
            typeof Header === "string"
              ? () => {
                  return Header;
                }
              : () => {
                  return Header({
                    toggleAllRowsSelected,
                    setFilters,
                    filterValues,
                    column: {
                      filterValue: filterValues[accessor],
                      setFilter: makeSetFilter(accessor),
                    },
                  });
                },
        };
        return result;
      },
    );
  }, [filterValues, toggleAllRowsSelected]);

  const dataToProps = useCallback((data: S[]) => {
    return data.map((cells, key) => {
      const path = [key];
      return {
        key: path.join("."),
        path,
        values: cells,
        isSelected: false,
      } satisfies Props<S>;
    });
  }, []);
  const [propsMap, setPropsMap] = useState(dataToProps(data));
  useEffect(() => {
    setPropsMap(dataToProps(data));
  }, [data, dataToProps]);
  const [rows, setRows] = useState<Rows<S>>([]);

  useEffect(() => {
    let newRows: Rows<S> = [];

    propsMap.forEach((props) => {
      const { path } = props;

      const cells: Cell[] = [];

      for (let i = 0; i < headers.length; i++) {
        const { key } = headers[i];
        const value = props.values[key];
        const column = columns.current[key];
        if (column === undefined) break;
        const { Cell, cellProps } = column;

        const contentProps = {
          cell: { value },
          row: props,
        };
        cells.push({
          key,
          props: contentProps,
          style: {
            textAlign: column.cellAlign,
            ...(cellProps?.style ?? {}),
          },
          Content:
            Cell ||
            (({ cell, row, ...thisProps }) => {
              return <div {...thisProps}>{value}</div>;
            }),
          render:
            Cell !== undefined ? Cell.bind(null, contentProps) : () => value,
        });
      }

      newRows.push({
        ...props,
        cells,
      });
      newRows = newRows.filter((row) => {
        return row.path.length === 1 || row.path[0] !== path[0];
      });
    });
    setRows(newRows);
  }, [headers, propsMap]);

  const toggleRowSelected = useCallback(
    (toggleRows: number[], flag?: boolean) => {
      setRows((prev) => {
        const copy = prev.slice();
        toggleRows.forEach((toggleRow) => {
          copy[toggleRow].isSelected =
            flag === undefined ? !copy[toggleRow].isSelected : flag;
        });
        return copy;
      });
    },
    [],
  );

  const [pageIndex, setPageIndex] = useState(
    props.initialState?.pageIndex ?? 0,
  );
  const [pageSize, setPageSize] = useState(props.initialState?.pageSize ?? 10);

  const canPreviousPage = useMemo(() => pageIndex !== 0, [pageIndex]);
  const canNextPage = useMemo(
    () => Math.floor(data.length / pageSize) > pageIndex + 1,
    [data.length, pageIndex, pageSize],
  );

  const nextPage = useCallback(() => {
    setPageIndex((prev) => prev + 1);
  }, []);
  const previousPage = useCallback(() => {
    setPageIndex((prev) => prev - 1);
  }, []);

  const [filteredRows, setFilteredRows] = useState<Rows<S>>([]);

  useEffect(() => {
    const fv = Object.entries(filterValues);
    setFilteredRows(
      rows.filter((row) => {
        const values = Object.entries(row.values);
        let flag = true;

        firstLoop: for (let i = 0; i < fv.length; i++) {
          const [accessor, filterValue] = fv[i];
          const filter = columns.current[accessor]?.filter;
          if (filter === undefined) {
            throw new Error(`Set columns.${accessor}.filter`);
          }

          for (let j = 0; j < values.length; j++) {
            const [key, value] = values[j];
            if (key !== accessor) continue;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            const filtered = filter(value, { column: { filterValue } });
            if (filtered === false) {
              flag = false;
              break firstLoop;
            }
          }
        }
        return flag;
      }),
    );
  }, [filterValues, rows]);

  const page = useMemo(
    () => filteredRows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [filteredRows, pageIndex, pageSize],
  );

  return {
    canPreviousPage,
    canNextPage,
    gotoPage: setPageIndex,
    nextPage,
    setPageSize,
    previousPage,
    pageIndex,
    headers,
    rows: filteredRows,
    page,
    toggleRowSelected,
    toggleAllRowsSelected,
  };
};
